import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../utils/axios';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  ViewKanban as KanbanIcon,
  CalendarMonth as CalendarIcon,
  Chat as ChatIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  ExitToApp as LogoutIcon,
  Notifications as NotificationsIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Archive as ArchiveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Group as GroupIcon,
  Send as SendIcon,
  Close as CloseIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import NotificationBell from './NotificationBell';

const drawerWidth = 280;

// Безопасная работа с localStorage для iOS Safari
const safeStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      // ignore
    }
  },
  getJSON: (key, defaultValue) => {
    try {
      const cached = localStorage.getItem(key);
      return cached ? JSON.parse(cached) : defaultValue;
    } catch (e) {
      return defaultValue;
    }
  }
};

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [boardsMenuName, setBoardsMenuName] = useState('Доски');
  const [renameMenuAnchor, setRenameMenuAnchor] = useState(null);
  const [openRenameDialog, setOpenRenameDialog] = useState(false);
  const [newBoardsName, setNewBoardsName] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openCreateBoardDialog, setOpenCreateBoardDialog] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [boardNames, setBoardNames] = useState(() => safeStorage.getJSON('boardNames', []));
  const [boardNamesLoaded, setBoardNamesLoaded] = useState(false);
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);
  const [duplicateBoardName, setDuplicateBoardName] = useState('');
  const [boardToDuplicate, setBoardToDuplicate] = useState('');
  const [expandChats, setExpandChats] = useState(false);
  const [managers, setManagers] = useState(() => safeStorage.getJSON('managers', []));
  const [chatRooms, setChatRooms] = useState(() => safeStorage.getJSON('chatRooms', []));
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [selectedChatRoom, setSelectedChatRoom] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newChatMessage, setNewChatMessage] = useState('');
  const [openGroupChatDialog, setOpenGroupChatDialog] = useState(false);
  const [groupChatName, setGroupChatName] = useState('');
  const [selectedChatManagers, setSelectedChatManagers] = useState([]);
  const [adminUser, setAdminUser] = useState(() => safeStorage.getJSON('adminUser', null));
  const [selectedChatFile, setSelectedChatFile] = useState(null);
  const [uploadingChatFile, setUploadingChatFile] = useState(false);
  const chatMessagesEndRef = React.useRef(null);
  const chatFileInputRef = React.useRef(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleOpenRenameMenu = (event) => {
    event.stopPropagation();
    setRenameMenuAnchor(event.currentTarget);
  };

  const handleCloseRenameMenu = () => {
    setRenameMenuAnchor(null);
  };

  const handleOpenRenameDialog = () => {
    setNewBoardsName(boardsMenuName);
    setOpenRenameDialog(true);
    handleCloseRenameMenu();
  };

  const handleCloseRenameDialog = () => {
    setOpenRenameDialog(false);
    setNewBoardsName('');
  };

  const handleSaveNewName = async () => {
    try {
      await axios.put('/api/settings/boardsMenuName', {
        value: newBoardsName
      });
      setBoardsMenuName(newBoardsName);
      handleCloseRenameDialog();
    } catch (error) {
      console.error('Ошибка сохранения названия:', error);
    }
  };

  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
    handleCloseRenameMenu();
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeleteBoard = async () => {
    handleCloseRenameMenu();
    
    if (!newBoardsName) return;
    
    if (window.confirm(`Удалить доску "${newBoardsName}" для всех менеджеров? Все карточки будут удалены.`)) {
      try {
        const boardsResponse = await axios.get('/api/boards');
        const boardsToDelete = boardsResponse.data.filter(board => board.name === newBoardsName);
        
        await Promise.all(
          boardsToDelete.map(board => axios.delete(`/api/boards/${board._id}`))
        );
        
        const updatedNames = boardNames.filter(name => name !== newBoardsName);
        setBoardNames(updatedNames);
        
        window.dispatchEvent(new CustomEvent('refreshBoards'));
        
        if (location.pathname.includes(encodeURIComponent(newBoardsName))) {
          navigate('/');
        }
      } catch (error) {
        console.error('Ошибка удаления доски:', error);
        alert('Ошибка при удалении доски');
      }
    }
  };

  const handleOpenCreateBoardDialog = () => {
    setOpenCreateBoardDialog(true);
    handleCloseRenameMenu();
  };

  const handleCloseCreateBoardDialog = () => {
    setOpenCreateBoardDialog(false);
    setNewBoardName('');
  };

  const handleCreateNewBoard = async () => {
    try {
      const managersResponse = await axios.get('/api/users/managers');
      const managers = managersResponse.data;
      
      await Promise.all(managers.map(manager => 
        axios.post('/api/boards', {
          name: newBoardName,
          manager: manager._id,
          columns: []
        })
      ));
      
      handleCloseCreateBoardDialog();
      window.dispatchEvent(new CustomEvent('refreshBoards'));
    } catch (error) {
      console.error('Ошибка создания доски:', error);
    }
  };

  const handleOpenDuplicateDialog = () => {
    setBoardToDuplicate(newBoardsName);
    setDuplicateBoardName(`${newBoardsName} (копия)`);
    setOpenDuplicateDialog(true);
    handleCloseRenameMenu();
  };

  const handleCloseDuplicateDialog = () => {
    setOpenDuplicateDialog(false);
    setDuplicateBoardName('');
    setBoardToDuplicate('');
  };

  const handleDuplicateBoard = async () => {
    try {
      const boardsResponse = await axios.get('/api/boards');
      const allBoards = boardsResponse.data;
      const sourceBoardsWithName = allBoards.filter(board => board.name === boardToDuplicate);
      
      for (const sourceBoard of sourceBoardsWithName) {
        const newBoard = await axios.post('/api/boards', {
          name: duplicateBoardName,
          manager: sourceBoard.manager._id,
          columns: sourceBoard.columns
        });

        const cardsResponse = await axios.get(`/api/boards/${sourceBoard._id}`);
        const cards = cardsResponse.data.cards;

        for (const card of cards) {
          await axios.post('/api/cards', {
            board: newBoard.data._id,
            columnId: card.columnId,
            company: card.company._id,
            title: card.title,
            description: card.description,
            priority: card.priority,
            color: card.color,
            dueDate: card.dueDate,
            position: card.position,
            stickers: card.stickers,
            tasks: card.tasks,
            notes: card.notes
          });
        }
      }

      handleCloseDuplicateDialog();
      window.dispatchEvent(new CustomEvent('refreshBoards'));
      navigate(`/kanban/${encodeURIComponent(duplicateBoardName)}`);
    } catch (error) {
      console.error('Ошибка дублирования доски:', error);
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get('/api/settings');
        if (response.data.boardsMenuName) {
          setBoardsMenuName(response.data.boardsMenuName);
        }
      } catch (error) {
        console.error('Ошибка загрузки настроек:', error);
      }
    };
    
    const fetchBoardNames = async () => {
      try {
        const response = await axios.get('/api/boards');
        const boards = response.data;
        const uniqueNames = [...new Set(boards.map(board => board.name))];
        setBoardNames(uniqueNames);
        setBoardNamesLoaded(true);
        safeStorage.setItem('boardNames', JSON.stringify(uniqueNames));
      } catch (error) {
        console.error('Ошибка загрузки досок:', error);
        setBoardNamesLoaded(true);
      }
    };
    
    fetchSettings();
    fetchBoardNames();

    if (isAdmin) {
      fetchManagers();
    } else {
      fetchAdmin();
    }
    fetchChatRooms();

    const handleRefreshBoards = () => {
      fetchBoardNames();
    };
    window.addEventListener('refreshBoards', handleRefreshBoards);
    
    const handleOpenChatFromNotification = async (event) => {
      const { chatRoomId } = event.detail;
      try {
        const response = await axios.get(`/api/chat-rooms/${chatRoomId}/messages`);
        const chatRoom = chatRooms.find(room => room._id === chatRoomId);
        if (chatRoom) {
          setSelectedChatRoom(chatRoom);
          setChatDrawerOpen(true);
          fetchChatMessages(chatRoomId);
        }
      } catch (error) {
        console.error('Ошибка открытия чата:', error);
      }
    };
    window.addEventListener('openChat', handleOpenChatFromNotification);
    
    return () => {
      window.removeEventListener('refreshBoards', handleRefreshBoards);
      window.removeEventListener('openChat', handleOpenChatFromNotification);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const fetchManagers = async () => {
    try {
      const response = await axios.get('/api/users/managers');
      setManagers(response.data);
      safeStorage.setItem('managers', JSON.stringify(response.data));
    } catch (error) {
      console.error('Ошибка загрузки менеджеров:', error);
    }
  };

  const fetchAdmin = async () => {
    try {
      const response = await axios.get('/api/users/admins');
      const admin = response.data[0];
      setAdminUser(admin);
      if (admin) {
        safeStorage.setItem('adminUser', JSON.stringify(admin));
      }
    } catch (error) {
      console.error('Ошибка загрузки админа:', error);
    }
  };

  const fetchChatRooms = async () => {
    try {
      const response = await axios.get('/api/chat-rooms');
      setChatRooms(response.data);
      safeStorage.setItem('chatRooms', JSON.stringify(response.data));
    } catch (error) {
      console.error('Ошибка загрузки чатов:', error);
    }
  };

  const handleOpenChat = async (manager) => {
    try {
      const response = await axios.post('/api/chat-rooms/direct', {
        participantId: manager._id
      });
      setSelectedChatRoom(response.data);
      setChatDrawerOpen(true);
      fetchChatMessages(response.data._id);
    } catch (error) {
      console.error('Ошибка открытия чата:', error);
    }
  };

  const fetchChatMessages = async (chatRoomId) => {
    try {
      const response = await axios.get(`/api/chat-rooms/${chatRoomId}/messages`);
      const newMessages = response.data;
      
      if (chatMessages.length > 0 && newMessages.length > chatMessages.length) {
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage.sender._id !== user.id) {
          playMessageSound();
        }
      }
      
      setChatMessages(newMessages);
    } catch (error) {
      console.error('Ошибка загрузки сообщений:', error);
    }
  };

  const playMessageSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 600;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.error('Ошибка воспроизведения звука:', error);
    }
  };

  useEffect(() => {
    if (selectedChatRoom && chatDrawerOpen) {
      const interval = setInterval(() => {
        fetchChatMessages(selectedChatRoom._id);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [selectedChatRoom, chatDrawerOpen]);

  useEffect(() => {
    if (chatMessagesEndRef.current) {
      chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  const handleChatFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedChatFile(file);
    }
  };

  const handleSendChatFile = async () => {
    if (!selectedChatFile || !selectedChatRoom) return;

    setUploadingChatFile(true);
    const formData = new FormData();
    formData.append('file', selectedChatFile);

    try {
      const uploadResponse = await axios.post('/api/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const fileData = uploadResponse.data;
      
      const response = await axios.post(`/api/chat-rooms/${selectedChatRoom._id}/messages`, {
        text: `📎 ${fileData.originalname}`,
        fileUrl: fileData.path,
        fileName: fileData.originalname,
        fileType: fileData.mimetype,
        fileSize: fileData.size
      });
      
      setChatMessages([...chatMessages, response.data]);
      setSelectedChatFile(null);
      if (chatFileInputRef.current) {
        chatFileInputRef.current.value = '';
      }
      
      setTimeout(() => {
        if (chatMessagesEndRef.current) {
          chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (error) {
      console.error('Ошибка загрузки файла:', error);
      alert('Ошибка загрузки файла');
    } finally {
      setUploadingChatFile(false);
    }
  };

  const handleSendChatMessage = async () => {
    if (selectedChatFile) {
      handleSendChatFile();
      return;
    }
    
    if (!newChatMessage.trim() || !selectedChatRoom) return;

    try {
      const response = await axios.post(`/api/chat-rooms/${selectedChatRoom._id}/messages`, {
        text: newChatMessage
      });
      setChatMessages([...chatMessages, response.data]);
      setNewChatMessage('');
      
      setTimeout(() => {
        if (chatMessagesEndRef.current) {
          chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (error) {
      console.error('Ошибка отправки сообщения:', error);
    }
  };

  const handleCreateGroupChat = async () => {
    try {
      const response = await axios.post('/api/chat-rooms/group', {
        name: groupChatName,
        participantIds: selectedChatManagers
      });
      setChatRooms([response.data, ...chatRooms]);
      setOpenGroupChatDialog(false);
      setGroupChatName('');
      setSelectedChatManagers([]);
      fetchChatRooms();
    } catch (error) {
      console.error('Ошибка создания группового чата:', error);
    }
  };

  const handleOpenGroupChat = (chat) => {
    setSelectedChatRoom(chat);
    setChatDrawerOpen(true);
    fetchChatMessages(chat._id);
  };

  const menuItems = useMemo(() => {
    const items = [
      { text: 'Дашборд', icon: <DashboardIcon />, path: '/', id: 'dashboard' },
      { text: 'Компании', icon: <BusinessIcon />, path: '/companies', id: 'companies' },
    ];

    if (boardNames.length > 0) {
      boardNames.forEach((boardName) => {
        items.push({
          text: boardName,
          icon: <KanbanIcon />,
          path: `/kanban/${encodeURIComponent(boardName)}`,
          isBoardItem: true,
          id: `board-${boardName}`
        });
      });
    } else if (isAdmin && boardNamesLoaded) {
      items.push({
        text: '+ Создать доску',
        icon: <AddIcon />,
        path: '#',
        isCreateButton: true,
        id: 'create-board'
      });
    }

    items.push(
      { text: 'Календарь', icon: <CalendarIcon />, path: '/calendar', id: 'calendar' }
    );

    items.push(
      { text: 'Архивы', icon: <ArchiveIcon />, path: '/archives', id: 'archives' }
    );

    items.push({ text: 'Чаты', icon: <ChatIcon />, path: '#', isChatsMenu: true, id: 'chats' });

    items.push(
      { text: 'Финансы', icon: <MoneyIcon />, path: '/finance', id: 'finance' }
    );

    if (isAdmin) {
      items.push(
        { text: 'Заявки', icon: <NotificationsIcon />, path: '/change-requests', id: 'change-requests' },
        { text: 'Пользователи', icon: <PeopleIcon />, path: '/users', id: 'users' },
        { text: 'Права доступа', icon: <SecurityIcon />, path: '/access-rights', id: 'access-rights' }
      );
    }

    return items;
  }, [boardNames, isAdmin, boardNamesLoaded]);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Header */}
      <Box
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, rgba(136, 192, 208, 0.15) 0%, rgba(94, 129, 172, 0.15) 100%)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 50%, rgba(136, 192, 208, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(136, 192, 208, 0.3)',
              animation: 'float 6s ease-in-out infinite',
              '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-5px)' },
              },
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0d1117' }}>
              N
            </Typography>
          </Box>
          <Box>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 800, 
                background: 'linear-gradient(135deg, #88c0d0 0%, #b48ead 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em',
              }}
            >
              Nord CRM
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: '0.1em' }}>
              УПРАВЛЕНИЕ ПРОДАЖАМИ
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.04)' }} />
      
      {/* Navigation */}
      <List sx={{ px: 2, py: 2, flex: 1, overflow: 'auto' }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.id}>
            <ListItem 
              disablePadding 
              sx={{ mb: 0.5 }}
              secondaryAction={
                isAdmin && item.isBoardItem ? (
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setNewBoardsName(item.text);
                      handleOpenRenameMenu(e);
                    }}
                    sx={{
                      color: location.pathname === item.path ? '#0d1117' : 'text.secondary',
                      opacity: 0.7,
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: 'rgba(255,255,255,0.1)',
                      }
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                ) : null
              }
            >
              <ListItemButton
                selected={!item.isChatsMenu && location.pathname === item.path}
                onClick={() => {
                  if (item.isCreateButton) {
                    handleOpenCreateBoardDialog();
                  } else if (item.isChatsMenu) {
                    setExpandChats(!expandChats);
                  } else {
                    navigate(item.path);
                  }
                  if (mobileOpen) handleDrawerToggle();
                }}
                sx={{
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
                    color: '#0d1117',
                    boxShadow: '0 4px 20px rgba(136, 192, 208, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5e81ac 0%, #b48ead 100%)',
                    },
                    '& .MuiListItemIcon-root': {
                      color: '#0d1117',
                    },
                  },
                  '&:hover': {
                    background: 'rgba(136, 192, 208, 0.1)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: location.pathname === item.path ? '#0d1117' : '#88c0d0',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{
                    fontWeight: location.pathname === item.path ? 700 : 500,
                    fontSize: '0.95rem',
                  }}
                />
                {item.isChatsMenu && (expandChats ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
              </ListItemButton>
            </ListItem>

            {/* Chat submenu */}
            {item.isChatsMenu && (
              <Box 
                sx={{ 
                  pl: 2, 
                  display: expandChats ? 'block' : 'none',
                  mt: 0.5,
                }}
              >
                {isAdmin ? (
                  <>
                    {managers.map((manager) => (
                      <ListItemButton
                        key={manager._id}
                        onClick={() => handleOpenChat(manager)}
                        sx={{ 
                          borderRadius: 2, 
                          mb: 0.5, 
                          pl: 2,
                          py: 1,
                          background: 'rgba(255, 255, 255, 0.02)',
                          '&:hover': {
                            background: 'rgba(136, 192, 208, 0.1)',
                          }
                        }}
                      >
                        <ListItemIcon>
                          <Avatar 
                            sx={{ 
                              width: 32, 
                              height: 32,
                              background: 'linear-gradient(135deg, #a3be8c 0%, #88c0d0 100%)',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                            }}
                          >
                            {manager.firstName[0]}{manager.lastName[0]}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText 
                          primary={`${manager.firstName} ${manager.lastName}`}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        />
                      </ListItemButton>
                    ))}

                    {chatRooms.filter(chat => chat.type === 'group').map((chat) => (
                      <ListItem
                        key={chat._id}
                        disablePadding
                        sx={{ mb: 0.5 }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (window.confirm(`Удалить чат "${chat.name}"?`)) {
                                try {
                                  await axios.delete(`/api/chat-rooms/${chat._id}`);
                                  fetchChatRooms();
                                  if (selectedChatRoom?._id === chat._id) {
                                    setChatDrawerOpen(false);
                                    setSelectedChatRoom(null);
                                  }
                                } catch (error) {
                                  console.error('Ошибка удаления чата:', error);
                                  alert(error.response?.data?.message || 'Ошибка удаления чата');
                                }
                              }
                            }}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': {
                                color: 'error.main',
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemButton
                          onClick={() => handleOpenGroupChat(chat)}
                          sx={{ 
                            borderRadius: 2, 
                            pl: 2,
                            background: 'rgba(255, 255, 255, 0.02)',
                            '&:hover': {
                              background: 'rgba(136, 192, 208, 0.1)',
                            }
                          }}
                        >
                          <ListItemIcon>
                            <Avatar 
                              sx={{ 
                                width: 32, 
                                height: 32,
                                background: 'linear-gradient(135deg, #b48ead 0%, #5e81ac 100%)',
                              }}
                            >
                              <GroupIcon sx={{ fontSize: 18 }} />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText 
                            primary={chat.name}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}

                    <ListItemButton
                      onClick={() => setOpenGroupChatDialog(true)}
                      sx={{ 
                        borderRadius: 2, 
                        mb: 0.5, 
                        pl: 2,
                        border: '1px dashed rgba(136, 192, 208, 0.3)',
                        '&:hover': {
                          background: 'rgba(136, 192, 208, 0.1)',
                          borderColor: '#88c0d0',
                        }
                      }}
                    >
                      <ListItemIcon>
                        <AddIcon sx={{ color: '#88c0d0' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Создать групповой чат"
                        primaryTypographyProps={{ 
                          variant: 'body2', 
                          fontWeight: 500,
                          color: '#88c0d0',
                        }}
                      />
                    </ListItemButton>
                  </>
                ) : (
                  <>
                    {adminUser && (
                      <ListItemButton
                        onClick={() => handleOpenChat(adminUser)}
                        sx={{ 
                          borderRadius: 2, 
                          mb: 0.5, 
                          pl: 2,
                          background: 'rgba(136, 192, 208, 0.1)',
                          border: '1px solid rgba(136, 192, 208, 0.2)',
                          '&:hover': {
                            background: 'rgba(136, 192, 208, 0.15)',
                          }
                        }}
                      >
                        <ListItemIcon>
                          <Avatar 
                            sx={{ 
                              width: 32, 
                              height: 32, 
                              background: 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                            }}
                          >
                            {adminUser.firstName?.[0]}{adminUser.lastName?.[0]}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText 
                          primary={`${adminUser.firstName} ${adminUser.lastName}`}
                          secondary="Администратор"
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                          secondaryTypographyProps={{ variant: 'caption', sx: { color: '#88c0d0' } }}
                        />
                      </ListItemButton>
                    )}

                    {chatRooms.filter(chat => chat.type === 'group').map((chat) => (
                      <ListItemButton
                        key={chat._id}
                        onClick={() => handleOpenGroupChat(chat)}
                        sx={{ 
                          borderRadius: 2, 
                          mb: 0.5, 
                          pl: 2,
                          background: 'rgba(255, 255, 255, 0.02)',
                          '&:hover': {
                            background: 'rgba(136, 192, 208, 0.1)',
                          }
                        }}
                      >
                        <ListItemIcon>
                          <Avatar 
                            sx={{ 
                              width: 32, 
                              height: 32,
                              background: 'linear-gradient(135deg, #b48ead 0%, #5e81ac 100%)',
                            }}
                          >
                            <GroupIcon sx={{ fontSize: 18 }} />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText 
                          primary={chat.name}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        />
                      </ListItemButton>
                    ))}
                  </>
                )}
              </Box>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontWeight: 600,
              background: 'linear-gradient(90deg, #e6edf3 0%, #88c0d0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {menuItems.find(item => item.path === location.pathname)?.text || 'Nord CRM'}
          </Typography>

          <NotificationBell />

          {/* Theme Toggle */}
          <Tooltip title={isDarkMode ? 'Светлая тема' : 'Тёмная тема'}>
            <IconButton
              onClick={toggleTheme}
              sx={{
                ml: 1,
                width: 40,
                height: 40,
                background: isDarkMode 
                  ? 'linear-gradient(135deg, rgba(235, 203, 139, 0.15) 0%, rgba(136, 192, 208, 0.15) 100%)'
                  : 'linear-gradient(135deg, rgba(94, 129, 172, 0.15) 0%, rgba(180, 142, 173, 0.15) 100%)',
                border: isDarkMode 
                  ? '1px solid rgba(235, 203, 139, 0.3)'
                  : '1px solid rgba(94, 129, 172, 0.3)',
                '&:hover': {
                  background: isDarkMode 
                    ? 'linear-gradient(135deg, rgba(235, 203, 139, 0.25) 0%, rgba(136, 192, 208, 0.25) 100%)'
                    : 'linear-gradient(135deg, rgba(94, 129, 172, 0.25) 0%, rgba(180, 142, 173, 0.25) 100%)',
                  transform: 'scale(1.1) rotate(15deg)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {isDarkMode ? (
                <LightModeIcon sx={{ color: '#ebcb8b', fontSize: 22 }} />
              ) : (
                <DarkModeIcon sx={{ color: '#5e81ac', fontSize: 22 }} />
              )}
            </IconButton>
          </Tooltip>

          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            <IconButton 
              onClick={handleMenu} 
              sx={{ 
                p: 0.5,
                border: '2px solid rgba(136, 192, 208, 0.3)',
                '&:hover': {
                  borderColor: '#88c0d0',
                }
              }}
            >
              <Avatar
                sx={{ 
                  width: 36,
                  height: 36,
                  background: 'linear-gradient(135deg, #b48ead 0%, #5e81ac 100%)',
                  fontWeight: 600,
                }}
                alt={user?.firstName}
              >
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 200,
                }
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#e6edf3' }}>
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography variant="caption" sx={{ color: '#88c0d0' }}>
                  {user?.role === 'admin' ? '👑 Администратор' : '👤 Менеджер'}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <MenuItem 
                onClick={handleLogout}
                sx={{
                  color: '#bf616a',
                  '&:hover': {
                    background: 'rgba(191, 97, 106, 0.1)',
                  }
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" sx={{ color: '#bf616a' }} />
                </ListItemIcon>
                Выйти
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          minHeight: '100vh',
        }}
      >
        {children}
      </Box>

      {/* Context menus and dialogs remain the same with updated styling */}
      <Menu
        anchorEl={renameMenuAnchor}
        open={Boolean(renameMenuAnchor)}
        onClose={handleCloseRenameMenu}
      >
        <MenuItem onClick={handleOpenRenameDialog}>
          <ListItemIcon>
            <EditIcon fontSize="small" sx={{ color: '#88c0d0' }} />
          </ListItemIcon>
          <ListItemText>Переименовать вкладку</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenCreateBoardDialog}>
          <ListItemIcon>
            <AddIcon fontSize="small" sx={{ color: '#a3be8c' }} />
          </ListItemIcon>
          <ListItemText>Создать новую доску</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenDuplicateDialog}>
          <ListItemIcon>
            <EditIcon fontSize="small" sx={{ color: '#b48ead' }} />
          </ListItemIcon>
          <ListItemText>Дублировать</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteBoard}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: '#bf616a' }} />
          </ListItemIcon>
          <ListItemText sx={{ color: '#bf616a' }}>Удалить доску</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <Dialog open={openRenameDialog} onClose={handleCloseRenameDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Переименовать вкладку</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Название"
            value={newBoardsName}
            onChange={(e) => setNewBoardsName(e.target.value)}
            margin="normal"
            placeholder="Например: Воронки, Проекты, Сделки"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseRenameDialog} variant="outlined">Отмена</Button>
          <Button onClick={handleSaveNewName} variant="contained" disabled={!newBoardsName}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCreateBoardDialog} onClose={handleCloseCreateBoardDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Создать новую доску</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Название доски"
            value={newBoardName}
            onChange={(e) => setNewBoardName(e.target.value)}
            margin="normal"
            placeholder="Например: Холодные звонки"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Доска будет создана для всех менеджеров без колонок. Каждый менеджер сможет добавить свои колонки.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseCreateBoardDialog} variant="outlined">Отмена</Button>
          <Button onClick={handleCreateNewBoard} variant="contained" disabled={!newBoardName}>
            Создать
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDuplicateDialog} onClose={handleCloseDuplicateDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Дублировать доску</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Название новой доски"
            value={duplicateBoardName}
            onChange={(e) => setDuplicateBoardName(e.target.value)}
            margin="normal"
            placeholder="Например: Продажи (копия)"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Будет создана копия доски "{boardToDuplicate}" со всеми колонками и карточками для всех менеджеров.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDuplicateDialog} variant="outlined">Отмена</Button>
          <Button onClick={handleDuplicateBoard} variant="contained" disabled={!duplicateBoardName}>
            Дублировать
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openGroupChatDialog} onClose={() => setOpenGroupChatDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Создать групповой чат</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Название чата"
            value={groupChatName}
            onChange={(e) => setGroupChatName(e.target.value)}
            margin="normal"
          />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
            Выберите участников:
          </Typography>
          <List sx={{ maxHeight: 200, overflow: 'auto' }}>
            {managers.map((manager) => (
              <ListItemButton
                key={manager._id}
                onClick={() => {
                  if (selectedChatManagers.includes(manager._id)) {
                    setSelectedChatManagers(selectedChatManagers.filter(id => id !== manager._id));
                  } else {
                    setSelectedChatManagers([...selectedChatManagers, manager._id]);
                  }
                }}
                sx={{ 
                  borderRadius: 2,
                  mb: 0.5,
                  background: selectedChatManagers.includes(manager._id) 
                    ? 'linear-gradient(135deg, rgba(136, 192, 208, 0.2) 0%, rgba(94, 129, 172, 0.2) 100%)'
                    : 'transparent',
                  border: selectedChatManagers.includes(manager._id) 
                    ? '1px solid rgba(136, 192, 208, 0.3)'
                    : '1px solid transparent',
                  '&:hover': {
                    background: selectedChatManagers.includes(manager._id) 
                      ? 'linear-gradient(135deg, rgba(136, 192, 208, 0.25) 0%, rgba(94, 129, 172, 0.25) 100%)'
                      : 'rgba(136, 192, 208, 0.1)',
                  }
                }}
              >
                <ListItemIcon>
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32,
                      background: selectedChatManagers.includes(manager._id)
                        ? 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)'
                        : 'rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    {manager.firstName[0]}{manager.lastName[0]}
                  </Avatar>
                </ListItemIcon>
                <ListItemText primary={`${manager.firstName} ${manager.lastName}`} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => {
            setOpenGroupChatDialog(false);
            setGroupChatName('');
            setSelectedChatManagers([]);
          }} variant="outlined">
            Отмена
          </Button>
          <Button 
            onClick={handleCreateGroupChat} 
            variant="contained" 
            disabled={!groupChatName || selectedChatManagers.length === 0}
          >
            Создать
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chat Drawer */}
      <Drawer
        anchor="right"
        open={chatDrawerOpen}
        onClose={() => setChatDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 420,
            maxWidth: '100%',
            background: isDarkMode ? 'rgba(13, 17, 23, 0.98)' : '#ffffff',
            backdropFilter: 'blur(30px)',
          },
        }}
      >
        {selectedChatRoom && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Chat Header */}
            <Box 
              sx={{ 
                p: 2, 
                background: isDarkMode 
                  ? 'linear-gradient(135deg, rgba(136, 192, 208, 0.1) 0%, rgba(94, 129, 172, 0.1) 100%)'
                  : 'linear-gradient(135deg, #5e81ac 0%, #88c0d0 100%)',
                borderBottom: '1px solid',
                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
                display: 'flex', 
                alignItems: 'center', 
                gap: 2 
              }}
            >
              <Avatar 
                sx={{ 
                  background: selectedChatRoom.type === 'group' 
                    ? 'linear-gradient(135deg, #b48ead 0%, #5e81ac 100%)'
                    : 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
                  width: 48,
                  height: 48,
                }}
              >
                {selectedChatRoom.type === 'group' ? <GroupIcon /> : (
                  selectedChatRoom.participants.find(p => p._id !== user.id)?.firstName[0] || 'U'
                )}
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: isDarkMode ? '#e6edf3' : '#0d1117' }}>
                  {selectedChatRoom.type === 'group' 
                    ? selectedChatRoom.name 
                    : `${selectedChatRoom.participants.find(p => p._id !== user.id)?.firstName} ${selectedChatRoom.participants.find(p => p._id !== user.id)?.lastName}`
                  }
                </Typography>
                {selectedChatRoom.type === 'group' && (
                  <Typography variant="caption" sx={{ color: isDarkMode ? '#88c0d0' : 'rgba(0, 0, 0, 0.7)' }}>
                    {selectedChatRoom.participants.length} участников
                  </Typography>
                )}
              </Box>
              {isAdmin && selectedChatRoom.type === 'group' && (
                <IconButton 
                  onClick={async () => {
                    if (window.confirm(`Удалить чат "${selectedChatRoom.name}"?`)) {
                      try {
                        await axios.delete(`/api/chat-rooms/${selectedChatRoom._id}`);
                        setChatDrawerOpen(false);
                        setSelectedChatRoom(null);
                        fetchChatRooms();
                      } catch (error) {
                        console.error('Ошибка удаления чата:', error);
                        alert(error.response?.data?.message || 'Ошибка удаления чата');
                      }
                    }
                  }}
                  sx={{ color: '#bf616a' }}
                >
                  <DeleteIcon />
                </IconButton>
              )}
              <IconButton onClick={() => setChatDrawerOpen(false)} sx={{ color: 'text.secondary' }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Messages */}
            <Box 
              sx={{ 
                flexGrow: 1, 
                overflow: 'auto', 
                p: 2, 
                background: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : '#f8fafc',
              }}
            >
              {chatMessages.length === 0 ? (
                <Box sx={{ textAlign: 'center', mt: 8 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    💬 Нет сообщений. Начните общение!
                  </Typography>
                </Box>
              ) : (
                chatMessages.map((msg) => (
                  <Box
                    key={msg._id}
                    sx={{
                      display: 'flex',
                      justifyContent: msg.sender._id === user.id ? 'flex-end' : 'flex-start',
                      mb: 2
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: '80%',
                        p: 2,
                        borderRadius: 3,
                        background: msg.sender._id === user.id 
                          ? 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)'
                          : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'),
                        color: msg.sender._id === user.id ? '#0d1117' : (isDarkMode ? '#e6edf3' : '#333'),
                        border: msg.sender._id === user.id 
                          ? 'none'
                          : (isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)'),
                        boxShadow: msg.sender._id !== user.id && !isDarkMode 
                          ? '0 2px 8px rgba(0, 0, 0, 0.08)' 
                          : 'none',
                      }}
                    >
                      {msg.sender._id !== user.id && selectedChatRoom.type === 'group' && (
                        <Typography 
                          variant="caption" 
                          fontWeight={700} 
                          display="block" 
                          sx={{ mb: 0.5, color: isDarkMode ? '#88c0d0' : '#5e81ac' }}
                        >
                          {msg.sender.firstName} {msg.sender.lastName}
                        </Typography>
                      )}
                      
                      {/* Отображение файла */}
                      {msg.fileUrl ? (
                        <Box>
                          {msg.fileType?.startsWith('image/') ? (
                            <Box
                              component="img"
                              src={`http://localhost:5001${msg.fileUrl}`}
                              alt={msg.fileName}
                              sx={{
                                maxWidth: '100%',
                                maxHeight: 200,
                                borderRadius: 1,
                                cursor: 'pointer',
                                mb: 0.5,
                              }}
                              onClick={() => window.open(`http://localhost:5001${msg.fileUrl}`, '_blank')}
                            />
                          ) : msg.fileType?.startsWith('video/') ? (
                            <video
                              controls
                              style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4 }}
                            >
                              <source src={`http://localhost:5001${msg.fileUrl}`} type={msg.fileType} />
                            </video>
                          ) : (
                            <Button
                              startIcon={<DescriptionIcon />}
                              variant="outlined"
                              size="small"
                              href={`http://localhost:5001${msg.fileUrl}`}
                              target="_blank"
                              sx={{ 
                                mb: 0.5,
                                borderColor: msg.sender._id === user.id ? 'rgba(0,0,0,0.3)' : undefined,
                                color: msg.sender._id === user.id ? '#0d1117' : undefined,
                              }}
                            >
                              {msg.fileName || 'Файл'}
                            </Button>
                          )}
                          <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                            {msg.text?.replace(/^📎\s*/, '')}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                          {msg.text}
                        </Typography>
                      )}
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          opacity: 0.7, 
                          display: 'block', 
                          mt: 0.5, 
                          fontSize: '0.7rem',
                          textAlign: 'right',
                        }}
                      >
                        {new Date(msg.createdAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
              <div ref={chatMessagesEndRef} />
            </Box>

            {/* Message Input */}
            <Box 
              sx={{ 
                p: 2, 
                background: isDarkMode ? 'rgba(22, 27, 34, 0.8)' : '#ffffff',
                borderTop: '1px solid',
                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
              }}
            >
              {/* Выбранный файл */}
              {selectedChatFile && (
                <Box 
                  sx={{ 
                    mb: 1.5, 
                    p: 1.5, 
                    background: isDarkMode ? 'rgba(136, 192, 208, 0.1)' : 'rgba(94, 129, 172, 0.1)',
                    border: '1px solid',
                    borderColor: isDarkMode ? 'rgba(136, 192, 208, 0.2)' : 'rgba(94, 129, 172, 0.2)',
                    borderRadius: 2, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1 
                  }}
                >
                  <AttachFileIcon fontSize="small" sx={{ color: isDarkMode ? '#88c0d0' : '#5e81ac' }} />
                  <Typography variant="body2" sx={{ flex: 1, color: 'text.primary' }} noWrap>
                    {selectedChatFile.name} ({(selectedChatFile.size / 1024).toFixed(1)} KB)
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => setSelectedChatFile(null)} 
                    sx={{ color: '#bf616a' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {/* Кнопка прикрепления файла */}
                <input
                  type="file"
                  ref={chatFileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleChatFileSelect}
                  accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                />
                <Tooltip title="Прикрепить файл или изображение" arrow>
                  <IconButton
                    onClick={() => chatFileInputRef.current?.click()}
                    disabled={uploadingChatFile}
                    sx={{ 
                      background: isDarkMode 
                        ? 'rgba(136, 192, 208, 0.15)' 
                        : 'rgba(94, 129, 172, 0.1)',
                      color: isDarkMode ? '#88c0d0' : '#5e81ac',
                      border: '2px solid',
                      borderColor: isDarkMode 
                        ? 'rgba(136, 192, 208, 0.4)' 
                        : 'rgba(94, 129, 172, 0.4)',
                      '&:hover': { 
                        background: isDarkMode 
                          ? 'rgba(136, 192, 208, 0.25)' 
                          : 'rgba(94, 129, 172, 0.2)',
                        borderColor: isDarkMode ? '#88c0d0' : '#5e81ac',
                      }
                    }}
                  >
                    <AttachFileIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                
                <TextField
                  fullWidth
                  size="small"
                  placeholder={selectedChatFile ? "Нажмите отправить для загрузки файла" : "Введите сообщение..."}
                  value={newChatMessage}
                  onChange={(e) => setNewChatMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendChatMessage();
                    }
                  }}
                  multiline
                  maxRows={3}
                  disabled={!!selectedChatFile}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : '#f8fafc',
                    }
                  }}
                />
                <IconButton 
                  onClick={handleSendChatMessage}
                  disabled={(!newChatMessage.trim() && !selectedChatFile) || uploadingChatFile}
                  sx={{
                    background: 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
                    color: '#0d1117',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5e81ac 0%, #b48ead 100%)',
                    },
                    '&:disabled': {
                      background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                      color: 'text.disabled',
                    }
                  }}
                >
                  {uploadingChatFile ? '...' : <SendIcon />}
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default Layout;
